import {PropsWithChildren, ReactNode, useCallback} from 'react';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import Loading from '@common/Loading';
import {useInfiniteScroll} from '@/hooks/useInfiniteScroll';
import Wrapper from '@common/pages/Wrapper';
import WidgetElementList from '@common/widgets/WidgetElementList';
import {WidgetHeader, WidgetHeaderAdditional, Content, BorderedContent} from '@common/widgets/WidgetPage.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import {Array} from "@svgdotjs/svg.js";
import {Roster} from "@/api/dto/dto";

type Props<T> = PropsWithChildren & {
  elements: T[];
  totalUnreadElements: number;
  dateProperty: string;
  title: string;
  isLoading: boolean;
  isApiLoading: boolean;
  fetchNextPage(): void;
  hasNextPage: boolean;
  isFetching: boolean;
  isRefetching: boolean;
  error: Error | null;
  markAllAsRead(): void;
  render(element: T): ReactNode;
  filterBar?: ReactNode;
  rosters?: Array<Roster> | undefined
}

export default function WidgetPage<T>({
  elements,
  totalUnreadElements,
  dateProperty,
  title,
  isLoading,
  isApiLoading,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isRefetching,
  error,
  markAllAsRead,
  render,
  filterBar,
  children,
}: Props<T>) {
  const { ref, listElement } = useInfiniteScroll<HTMLDivElement>({
    enabled: !isLoading && !!elements,
    fetching: isFetching,
    selector: '[data-widget-item]',
    onNextPage() {
      if (hasNextPage) {
        fetchNextPage();
      }
    },
  });

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  return (
    <Wrapper>
      <WidgetHeader>
        <WidgetHeaderAdditional>{children}</WidgetHeaderAdditional>
      </WidgetHeader>
      {filterBar}
      <Content>
        <BorderedContent>
          {error && <Banner type={BannerTypes.WARNING}>{DisplayHelpers.displayErrorMessage(error)}</Banner>}
          {isLoading && <Loading />}
          {!isLoading && elements && (
            <>
              <div ref={listElement}>
                <WidgetElementList
                  elements={elements}
                  totalUnreadElements={totalUnreadElements}
                  dateProperty={dateProperty}
                  title={title}
                  isApiLoading={isApiLoading}
                  markAllAsRead={handleMarkAllAsRead}
                  render={render}

                />
              </div>
              <span ref={ref} />
            </>
          )}
          {isFetching && !isRefetching && !isLoading && <Loading />}
        </BorderedContent>
      </Content>
    </Wrapper>
  );
}
